import React, { Suspense, useMemo } from 'react';
import Box from '@mui/material/Box';
import { Outlet, useLocation, useParams } from 'react-router';
import { hypertrackerTheme } from '@/globalStyles/theme';
import {
    BottomGreenLight,
    BoxGreenLight,
    OutletWrapper,
    ProspectiveGrid,
    TopGreenLight,
    Wrapper,
} from '@/components/Hypertracker/Layout/LayoutStyled';
import Header from '@/components/Hypertracker/Header';
import HyperTrackerFooter from '@/components/Hypertracker/HyperTrackerFooter';
import '@/globalStyles/hide-captcha.scss';
import AsideMenu from '@/components/Hypertracker/AsideMenu';
import HyperSearchMobile from '@/components/Hypertracker/HyperSearch/HyperSearchMobile';
import Spinner from '@/components/Spinner';
import HypertrackerSidebar from '@/components/Hypertracker/HypertrackerSidebar/HypertrackerSidebar';
import { buildersDataByAddress } from '@/lib/constants/hyperTrackerBuildersConstants';
import 'driver.js/dist/driver.css';
import '../HypertrackerCapture/capture-mode.scss';
import CaptureBanner from '@/components/Hypertracker/HypertrackerCapture/CaptureBanner';
import { CaptureProvider } from '@/components/Hypertracker/HypertrackerCapture/CaptureProvider';
import useInitialHypertrackerData from '@/hooks/useInitialHypertrackerData';
import InitialLoading from '@/components/InitialLoading';
import lazyRetry from '@/lib/helpers/lazyRetry';
import { useFavicon } from '@/hooks/useFavicon';
import useHypertrackerTitle from '@/hooks/useHypertrackerTitle';
import { AlphafeedToastProvider } from '@/components/Hypertracker/AlphaFeedToast';

const CommonHTModal = lazyRetry(
    () => import('@/components/Hypertracker/CommonHTModal'),
    'CommonHTModal',
);

const HypertrackerLayout = () => {
    useFavicon('/ht-favicon/favicon-32x32.png');
    useHypertrackerTitle();
    useInitialHypertrackerData();
    const { pathname } = useLocation();
    const { builder } = useParams();

    const isWalletPage = useMemo(() => {
        return /^\/hypertracker\/wallet\/[^/]+$/.test(pathname);
    }, [pathname]);

    const isApiPage = useMemo(() => {
        return pathname === '/hypertracker/api';
    }, [pathname]);

    const isGreenLightVisible = useMemo(() => {
        return (
            !pathname.startsWith('/hypertracker/heatmap') &&
            !pathname.startsWith('/hypertracker/openai-trial') &&
            !pathname.startsWith('/hypertracker/alphafeed')
        );
    }, [pathname]);

    const greelightBoxSx = useMemo(() => {
        return pathname.includes('stats') ? { position: 'fixed' } : { position: 'absolute' };
    }, [pathname]);

    const excludeBottomPadding = isWalletPage || isApiPage || pathname.includes('alphafeed');

    return (
        <AlphafeedToastProvider>
            <Wrapper>
                <HypertrackerSidebar />
                <Suspense fallback={<InitialLoading />}>
                    <CommonHTModal />
                </Suspense>
                <Header />
                <AsideMenu />
                <HyperSearchMobile />
                {/*<MagicBanner />*/}
                {pathname.includes('stats') && (
                    <ProspectiveGrid
                        sx={{
                            transform: 'perspective(800px) rotateX(-65deg)',
                            top: '-70px',
                            transformOrigin: 'center bottom',
                            '&::after': {
                                background: 'linear-gradient(transparent, black)',
                            },
                        }}
                    />
                )}
                <BoxGreenLight sx={greelightBoxSx}>
                    <TopGreenLight
                        sx={{
                            backgroundColor: builder
                                ? buildersDataByAddress?.[builder.toLowerCase()]?.color
                                : '',
                            width: isWalletPage ? '1000px' : '100%',
                            alignSelf: 'center',
                            visibility: isGreenLightVisible ? 'visible' : 'hidden',
                        }}
                    />
                </BoxGreenLight>
                {pathname.includes('stats') && (
                    <>
                        <ProspectiveGrid />
                        <Box
                            sx={{
                                position: 'fixed',
                                width: '100%',
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'center',
                                bottom: 0,
                                left: 0,
                            }}
                        >
                            <BottomGreenLight />
                        </Box>
                    </>
                )}
                <CaptureProvider containerSelector={'#content-zone'} scale={1}>
                    <OutletWrapper
                        sx={{
                            paddingBottom: excludeBottomPadding ? 0 : '100px',
                        }}
                    >
                        <CaptureBanner />
                        <Suspense fallback={<InitialLoading />}>
                            <Outlet />
                        </Suspense>
                    </OutletWrapper>
                </CaptureProvider>
                <HyperTrackerFooter isMobileVisible={false} />
                <Spinner color={hypertrackerTheme.palette.hyperLiquidColor} />
            </Wrapper>
        </AlphafeedToastProvider>
    );
};

export default HypertrackerLayout;
